import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    Renderer2,
    RendererStyleFlags2,
    ViewChild
} from '@angular/core';
import { IProductConfigurableSelection, IProductDataV2 } from '@df/catalog/product/product';
import { Client } from '@df/core/service/client';
import { DfEventService } from '@df/core/service/df-event';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { Customer } from '@df/session/customer/customer.interfaces';
import { WishlistToggleComponentDef } from '@df/session/wishlist/wishlist-toggle.component.def';
import { AttributeLabelPipeService } from '@df/ui/common/attribute-label.pipe';
import { gsap } from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import head from 'lodash-es/head';
import uniq from 'lodash-es/uniq';
import { take } from 'rxjs/internal/operators/take';
import { ProductDecorator } from '../../../model/product/product.decorator';
import {
    PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
    PRODUCT_WISHLIST_ADD_EVENT_NAME,
    PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME,
    PRODUCT_WISHLIST_PREPARE_PREVIEW_EVENT_NAME,
    ProductEventData
} from '../../product/product.abstract';
import { ProductService } from '../../product/product.service';
import { WishlistStatus } from '../wishlist.interface';

@Directive()
export abstract class WishlistToggleAbstract extends WishlistToggleComponentDef implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true })
    set product(value: ProductDecorator<IProductDataV2>) {
        this.productId = value.id;
        this._product = value;
    }

    get product(): ProductDecorator<IProductDataV2> {
        return this._product;
    }

    protected _product!: ProductDecorator<IProductDataV2>;

    @Input()
    selection?: IProductConfigurableSelection;

    @Input()
    listPosition?: number;

    @ViewChild('buttonRef', { read: ElementRef })
    buttonRef!: ElementRef;

    @ViewChild('iconRef', { read: ElementRef })
    iconRef!: ElementRef;

    @ViewChild('iconHeartLeftRef', { read: ElementRef })
    iconHeartLeftRef!: ElementRef;

    @ViewChild('iconHeartRightRef', { read: ElementRef })
    iconHeartRightRef!: ElementRef;

    protected tween?: gsap.core.Timeline;
    protected tweenLeft?: gsap.core.Timeline;
    protected tweenRight?: gsap.core.Timeline;

    remove: () => void;
    isLoading = false;

    private _wishlistCloseTimeout?: number;

    protected dfEventService = inject(DfEventService);
    protected renderer = inject(Renderer2);
    protected client = inject(Client);
    protected attributeLabelPipeService = inject(AttributeLabelPipeService);
    protected docRef = inject(DOCUMENT);
    protected elementRef = inject(ElementRef);
    protected productService = inject(ProductService);
    protected readonly disposableTimeoutService = injectTimeoutService();
    protected readonly isPlatformBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    constructor(protected changeDetectorRef: ChangeDetectorRef) {
        super(changeDetectorRef);

        this.remove = this.disposableTimeoutService.throttle(() => this._remove(), 300);

        this.subscription.add(this.status.subscribe(() => this.detectChanges()));
    }

    override ngOnInit() {
        this.registerGsapAnimation();

        this.subscription.add(
            this.dfEventService.success(event => {
                if (this.productService.canTriggerEvent(event, this.product, this.listPosition)) {
                    if (event.id === PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME) {
                        this.onOpenEvent(event.data);
                    }

                    if (event.id === PRODUCT_CLOSE_PREVIEW_EVENT_NAME) {
                        this.onCloseEvent(event.data);
                    }

                    if (event.id === PRODUCT_WISHLIST_ADD_EVENT_NAME) {
                        this.onAddEvent(event.data);
                    }
                }
            })
        );

        super.ngOnInit();
    }

    override ngOnDestroy(): void {
        this.tween?.kill();
        this.tweenLeft?.kill();
        this.tweenRight?.kill();

        super.ngOnDestroy();
    }

    /**
     * Checks if item is in wishlist already
     */
    override get inWishlist(): boolean {
        return this.isLoading || this.hasAnyInWishlist || super.inWishlist;
    }

    get hasAnyInWishlist(): boolean {
        if (this.listPosition === undefined) {
            return false;
        }

        return !!this.product.parent.children.find(product => (this.wishlist.getItemsByProduct(product.id) || []).length > 0);
    }

    /**
     * Open modal
     */
    handleClick() {
        this.isLoading = false;

        this.status.busy = true;

        const missingAttrCode = this.needSelectAttribute();

        if (!this.inWishlist) {
            if (missingAttrCode) {
                if (missingAttrCode === 'size') {
                    this.isLoading = true;

                    this.dfEventService.dispatch({
                        id: PRODUCT_WISHLIST_PREPARE_PREVIEW_EVENT_NAME,
                        data: {
                            product: this.product,
                            index: this.listPosition,
                            status: WishlistStatus.BeforeAdd
                        },
                        action: () => {
                            clearTimeout(this._wishlistCloseTimeout);
                            this._wishlistCloseTimeout = undefined;
                        }
                    });
                } else {
                    this.status.error = `Please select ${this.attributeLabelPipeService.execute(missingAttrCode)}`;
                }
            } else {
                this.isLoading = false;

                this.toggle();
            }
        } else {
            this.remove();
        }
    }

    protected override _toggle(): Promise<Customer.ILoadResponse> | void {
        const product = (this.item?.product || this.product) as ProductDecorator<IProductDataV2>;

        return this.wishlist.toggle(this.status, product.id, undefined, () => {
            this.dfEventService.dispatch({
                id: PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME,
                data: {
                    product,
                    index: this.listPosition,
                    status: WishlistStatus.AddSuccess
                },
                action: () => {
                    this.reset();
                }
            });
        });
    }

    protected onOpenEvent(data: ProductEventData) {
        this.isLoading = true;

        this.status.busy = !!data.loading;
    }

    protected onCloseEvent(data: ProductEventData) {
        this.isLoading = false;

        this.status.reset();
    }

    protected onAddEvent(data: ProductEventData) {
        if (data.option) {
            this.wishlist.toggle(this.status, data.option.product.id, undefined, () => {
                this.dfEventService.dispatch({
                    id: PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME,
                    data: {
                        ...data,
                        status: WishlistStatus.AddSuccess
                    },
                    action: () => {
                        this.reset();
                    }
                });
            });
        } else {
            this.isLoading = false;
            this.status.reset();
        }
    }

    /**
     * Remove items from wishlist
     */
    protected _remove() {
        const itemId = head(this.wishlist.getItemsByProduct(this.product.id))?.data?.id;
        const items = itemId ? [itemId] : [];

        this.product.parent.childrenIds.map(id => {
            const _items = this.wishlist.getItemsByProduct(id)?.map(item => item.data.id);

            if (_items?.length) {
                items.push(..._items);
            }
        });

        this.wishlist.remove(this.status, uniq(items), () => {
            this.reset();

            this.onRemove();
        });
    }

    protected needSelectAttribute(): string | undefined {
        const selection = this.selection || {};

        if (this.product?.isConfigurable && !!Object.keys(selection).length) {
            return this.product.configurableAttributeCodes?.find(i => !selection[i]);
        }

        return undefined;
    }

    protected reset() {
        clearTimeout(this._wishlistCloseTimeout);
        this._wishlistCloseTimeout = undefined;

        this.status.reset();

        this._wishlistCloseTimeout = this.timeoutService.setTimeout(() => {
            this.dfEventService.dispatch({
                id: PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
                data: {
                    product: this.product,
                    index: this.listPosition
                }
            });
        }, 5000);
    }

    protected registerGsapAnimation() {
        if (this.isPlatformBrowser) {
            gsap.registerPlugin(MotionPathPlugin);
        }
    }

    protected onRemove() {
        if (this.app.isPlatformBrowser) {
            const button = this.buttonRef?.nativeElement;
            const icon = this.iconRef?.nativeElement;
            const iconRect = icon?.getBoundingClientRect();
            const iconHeartLeft = this.iconHeartLeftRef?.nativeElement;
            const iconHeartRight = this.iconHeartRightRef?.nativeElement;

            this.tween?.kill();
            this.tweenLeft?.kill();
            this.tweenRight?.kill();

            this.tween = gsap.timeline();
            this.tweenLeft = gsap.timeline();
            this.tweenRight = gsap.timeline();

            const pathLeft = [
                { x: 0, y: 0 },
                { x: -3, y: -30 },
                { x: -10, y: 50 },
                { x: 1, y: 100 }
            ];
            const pathRight = [
                { x: 0, y: 0 },
                { x: 5, y: -50 },
                { x: 10, y: 50 },
                { x: 3, y: 100 }
            ];

            if (iconHeartLeft) {
                this.renderer.removeClass(iconHeartLeft, 'ng-hide');
            }

            if (iconHeartRight) {
                this.renderer.removeClass(iconHeartRight, 'ng-hide');
            }

            if (button) {
                this.renderer.setStyle(button, '--wishlist-icon-x', `${iconRect.x}px`, RendererStyleFlags2.DashCase);
                this.renderer.setStyle(button, '--wishlist-icon-y', `${iconRect.y}px`, RendererStyleFlags2.DashCase);
            }

            this.tween
                .to(icon, {
                    duration: 0,
                    opacity: 0
                })
                .to(icon, {
                    delay: 1,
                    duration: 0.5,
                    opacity: 1
                });

            this.tweenLeft
                .to(iconHeartLeft, {
                    duration: 0,
                    scale: 1,
                    opacity: 1
                })
                .to(iconHeartLeft, {
                    duration: 8,
                    ease: 'back.out(4)',
                    rotation: '-30',
                    opacity: 0,
                    motionPath: {
                        path: pathLeft,
                        type: 'cubic'
                    }
                })
                .to(iconHeartLeft, {
                    duration: 0.5,
                    scale: 0
                })
                .then(() => {
                    this.renderer.addClass(iconHeartLeft, 'ng-hide');
                    this.tweenLeft?.progress(0).kill();
                });

            this.tweenRight
                .to(iconHeartRight, {
                    duration: 0,
                    scale: 1,
                    opacity: 1
                })
                .to(iconHeartRight, {
                    duration: 8,
                    ease: 'back.out(4)',
                    rotation: '40',
                    opacity: 0,
                    motionPath: {
                        path: pathRight,
                        type: 'cubic'
                    }
                })
                .to(iconHeartRight, {
                    duration: 0.5,
                    scale: 0
                })
                .then(() => {
                    this.renderer.addClass(iconHeartRight, 'ng-hide');
                    this.tweenRight?.progress(0).kill();
                });

            this.subscription.add(
                this.client.obs.scroll.pipe(take(1)).subscribe(() => {
                    this.tween?.progress(1);
                    this.tweenLeft?.progress(1);
                    this.tweenRight?.progress(1);
                })
            );
        }
    }
}
